import { useEffect } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/site/Nav";
import Footer from "../components/site/Footer";
import { useT } from "../i18n/LanguageContext";
import { track } from "../lib/analytics";
import { getPosts } from "../lib/blog";

const SITE_URL = "https://gfirem.com";

function useBlogIndexHead(t) {
    useEffect(() => {
        const title =
            t("blog.metaTitle") ||
            "Blog · Custom software for small businesses | gfirem";
        const description =
            t("blog.metaDescription") ||
            "Field notes on building custom software for small businesses: honest guides, comparisons and case-study lessons from Austin, TX.";
        const canonical = `${SITE_URL}/blog`;

        const prev = document.title;
        document.title = title;

        const descEl = document.head.querySelector('meta[name="description"]');
        const prevDesc = descEl?.getAttribute("content") ?? null;
        descEl?.setAttribute("content", description);

        const canonicalEl = document.head.querySelector('link[rel="canonical"]');
        const prevCanonical = canonicalEl?.getAttribute("href") ?? null;
        canonicalEl?.setAttribute("href", canonical);

        return () => {
            document.title = prev;
            if (prevDesc !== null) descEl?.setAttribute("content", prevDesc);
            if (prevCanonical !== null)
                canonicalEl?.setAttribute("href", prevCanonical);
        };
    }, [t]);
}

export default function BlogIndex() {
    const { t, lang } = useT();
    useBlogIndexHead(t);

    // Prefer posts in the user's language. If none exist yet (e.g. only EN
    // posts published so far), fall back to EN and show a small notice so ES
    // visitors don't hit an empty page.
    const localizedPosts = getPosts({ lang });
    const usingFallback = lang !== "en" && localizedPosts.length === 0;
    const posts = usingFallback ? getPosts({ lang: "en" }) : localizedPosts;

    useEffect(() => {
        track("blog_index_viewed", {
            lang,
            post_count: posts.length,
            fallback_lang: usingFallback ? "en" : null,
        });
    }, [lang, posts.length, usingFallback]);

    return (
        <main className="min-h-screen bg-[var(--sand)] text-[var(--ink)]" data-testid="blog-index-page">
            <Nav />

            <section className="mx-auto max-w-5xl px-6 pt-24 sm:pt-32 pb-16">
                <p
                    className="overline"
                    data-testid="blog-overline"
                >
                    {t("blog.overline") || "Field notes"}
                </p>
                <h1
                    className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight max-w-3xl"
                    data-testid="blog-index-title"
                >
                    {t("blog.title") ||
                        "Honest guides for small-business owners weighing custom software."}
                </h1>
                <p
                    className="mt-6 text-base sm:text-lg text-[var(--ink)]/80 max-w-2xl leading-relaxed"
                    data-testid="blog-index-lede"
                >
                    {t("blog.lede") ||
                        "Comparisons, buying guides and lessons from 20+ years of shipping tools that pay for themselves."}
                </p>
            </section>

            <section className="mx-auto max-w-5xl px-6 pb-32" data-testid="blog-list">
                {usingFallback && (
                    <p
                        className="mb-8 font-mono text-xs tracking-widest uppercase text-[var(--ink)]/60 border border-warm inline-block px-3 py-2"
                        data-testid="blog-fallback-notice"
                    >
                        {t("blog.fallbackNotice") ||
                            "Notas de campo próximamente en español · mostrando la versión original en inglés"}
                    </p>
                )}
                {posts.length === 0 ? (
                    <p
                        className="font-mono text-sm text-[var(--ink)]/60 border-t border-[var(--ink)]/20 pt-8"
                        data-testid="blog-empty"
                    >
                        {t("blog.empty") ||
                            "First article coming soon — check back this week."}
                    </p>
                ) : (
                    <ul className="border-t border-[var(--ink)]/20">
                        {posts.map((post, idx) => (
                            <li
                                key={post.slug}
                                className="border-b border-[var(--ink)]/20"
                                data-testid={`blog-item-${idx}`}
                            >
                                <Link
                                    to={`/blog/${post.slug}`}
                                    data-cta-source={`blog-index-item-${post.slug}`}
                                    data-testid={`blog-item-${idx}-link`}
                                    onClick={() =>
                                        track("blog_post_opened", {
                                            slug: post.slug,
                                            source: "blog_index",
                                        })
                                    }
                                    className="group grid grid-cols-12 gap-4 items-baseline py-8 sm:py-10 hover:bg-[var(--ink)]/[0.03] transition-colors -mx-3 px-3"
                                >
                                    <span className="col-span-3 sm:col-span-2 font-mono text-xs text-[var(--ink)]/60 tracking-widest">
                                        {post.date}
                                    </span>
                                    <div className="col-span-9 sm:col-span-10">
                                        <h2 className="font-serif text-2xl sm:text-3xl leading-tight tracking-tight group-hover:text-terracotta transition-colors">
                                            {post.title}
                                        </h2>
                                        <p className="mt-3 text-sm sm:text-base text-[var(--ink)]/70 leading-relaxed max-w-3xl">
                                            {post.description}
                                        </p>
                                        <div className="mt-4 flex flex-wrap gap-2 items-center">
                                            <span className="text-[0.65rem] font-mono tracking-wide uppercase text-[var(--ink)]/50">
                                                {post.readingMinutes} min read
                                            </span>
                                            {post.tags.slice(0, 3).map((tag) => (
                                                <span
                                                    key={tag}
                                                    className="text-[0.65rem] font-mono tracking-wide uppercase border border-warm text-ink/60 px-2 py-0.5"
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <Footer />
        </main>
    );
}
